import xhr from 'xhr';

export const MONTHS_CATALOG = [
  { id: 1, name: 'Enero' },
  { id: 2, name: 'Febrero' },
  { id: 3, name: 'Marzo' },
  { id: 4, name: 'Abril' },
  { id: 5, name: 'Mayo' },
  { id: 6, name: 'Junio' },
  { id: 7, name: 'Julio' },
  { id: 8, name: 'Agosto' },
  { id: 9, name: 'Septiembre' },
  { id: 10, name: 'Octubre' },
  { id: 11, name: 'Noviembre' },
  { id: 12, name: 'Diciembre' },
];

/** Devuelve el rango 1-6, 7-12 o 1-12 según el sufijo del semestre. */
export function cycleMonthsForSemesterName(semesterName) {
  const s = String(semesterName || '').trim();
  if (s.length >= 6) {
    const suffix = s.slice(-2);
    if (suffix === '01') return { start: 1, end: 6 };
    if (suffix === '02') return { start: 7, end: 12 };
  }
  return { start: 1, end: 12 };
}

/** Meses del catálogo filtrados por el ciclo del semestre. */
export function monthsInCycle(semesterName) {
  const { start, end } = cycleMonthsForSemesterName(semesterName);
  return MONTHS_CATALOG.filter((m) => m.id >= start && m.id <= end);
}

/** "YYYYNN" derivado de una fecha (H1 → 01, H2 → 02). */
export function deriveSemesterNameFromDate(date) {
  if (!date) return null;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth() + 1;
  return `${year}${month <= 6 ? '01' : '02'}`;
}

/** Todos los month_students de una matrícula, con semester y months populados. */
export async function fetchMonthStudentsByEnrollment(type, enrollmentId) {
  if (!type || !enrollmentId) return [];
  const res = await xhr(
    `/month-students?filters[${type}_enrollment][id][$eq]=${enrollmentId}&populate=*&pagination[limit]=-1`
  );
  return res.data?.data ?? [];
}

/** Trae el catálogo de semestres. */
export async function fetchAllSemesters() {
  const res = await xhr(
    `/semesters?pagination[limit]=-1&sort[0]=name:desc`
  );
  return res.data?.data ?? [];
}

/**
 * Trae todas las matrículas activas del estudiante en los 3 tipos, normalizadas.
 * Cada elemento: { type, enrollmentId, program, programType, semester, isSemesterBased, status, raw }.
 */
export async function fetchAllStudentEnrollments(studentId) {
  if (!studentId) return [];
  const [grades, technical, courses] = await Promise.all([
    xhr(`/grade-enrollments?filters[student][id][$eq]=${studentId}&filters[active][$eq]=true&populate[grade][populate]=program_type&populate=semester&pagination[limit]=-1&sort[0]=updatedAt:desc`),
    xhr(`/technical-career-enrollments?filters[student][id][$eq]=${studentId}&filters[active][$eq]=true&populate[technical_career][populate]=program_type&populate=semester&pagination[limit]=-1&sort[0]=updatedAt:desc`),
    xhr(`/course-enrollments?filters[student][id][$eq]=${studentId}&filters[active][$eq]=true&populate[course][populate]=program_type&populate=semester&pagination[limit]=-1&sort[0]=updatedAt:desc`),
  ]);

  const normalize = (rows, type, programKey) =>
    (rows?.data?.data || []).map((enr) => ({
      type,
      enrollmentId: enr.id,
      program: enr[programKey] || null,
      programType: enr[programKey]?.program_type || null,
      semester: enr.semester || null,
      isSemesterBased: enr[programKey]?.program_type?.is_semester_based === true,
      status: enr.status || null,
      raw: enr,
    }));

  return [
    ...normalize(grades, 'grade', 'grade'),
    ...normalize(technical, 'technical_career', 'technical_career'),
    ...normalize(courses, 'course', 'course'),
  ].filter((e) => e.program?.id);
}

/** Resuelve una matrícula por (student, type, programId, programTypeId). */
export async function fetchEnrollment(type, studentId, programId, programTypeId) {
  const res = await xhr(
    `/${type.replace('_', '-')}-enrollments?filters[student][id][$eq]=${studentId}` +
    `&filters[${type}][id][$eq]=${programId}` +
    `&filters[${type}][program_type][id][$eq]=${programTypeId}` +
    `&populate=${type}&populate=semester&sort[0]=updatedAt:desc`
  );
  return res.data?.data?.[0] ?? null;
}

export async function fetchEnrollmentById(type, enrollmentId) {
  const res = await xhr(
    `/${type.replace('_', '-')}-enrollments/${enrollmentId}?populate=${type}&populate=semester&populate[student]=true`
  );
  return res.data?.data ?? null;
}

/**
 * Upsert por (matrícula, semestre) contra el endpoint custom del API.
 * Devuelve el registro creado/actualizado.
 *
 * promoteOrphan: si true, el backend absorbe el ms sin semestre (orphan) de la matrícula
 * dentro del registro (matrícula, semestre) que se está guardando. Se usa cuando el
 * usuario asigna un semestre a un chuleo histórico sin semestre.
 */
export async function saveMonths({ studentId, enrollmentType, enrollmentId, semesterId, months, on_day, promoteOrphan }) {
  const res = await xhr.put('/month-students/upsert', {
    data: {
      student: studentId,
      enrollment_type: enrollmentType,
      enrollment_id: enrollmentId,
      semester_id: semesterId,
      months: (months || []).map((m) => (typeof m === 'object' ? m.id : m)),
      // undefined → el backend no toca on_day (check oculto o reemplazado).
      ...(on_day === undefined ? {} : { on_day: !!on_day }),
      ...(promoteOrphan ? { promote_orphan: true } : {}),
    },
  });
  return res.data?.data ?? null;
}
